import { Dispatch, SetStateAction, useState } from 'react';
import {
  FileEvidenceForDataDeletionType,
  RemovePIIReportEvents,
} from 'src/components/pages/Case/Case.slice';
import { FormattedNamedOffender } from 'src/components/pages/Case/helpers/namedOffender';
import {
  DataDeletionType,
  EventDeletionType,
  Maybe,
  ParsedEvidenceDetailsType,
  TextRange,
} from 'src/graphql';
import { FormState } from 'src/types';
import { Accordion } from '../../Accordion';
import { DefaultOption, DropdownSelect } from '../../DropdownSelect';
import { EmptyState } from '../../EmptyState';
import { InformationBanner } from '../../InformationBanner';
import { Table } from '../../Table';
import {
  FieldWithTextRange,
  TextDeletionInput,
} from '../../TextDeletionInput/TextDeletionInput';
import { UploadedFiles } from '../components/UploadedFiles/UploadedFiles';
import {
  SectionDescription,
  StepperModalHeader,
} from '../RemovePIIDataModal.styles';
import {
  EmptyStateContainer,
  EventDropdownContainer,
  EventEvidenceSection,
  InformationBannerContainer,
  ReportDetailsHeaderContainer,
  ReportDetailsHeaderTop,
  ReportDetailsSection,
  SectionTitle,
} from './ReportDetailsStep.styles';
import { InfoBannerText } from './ReporterInfoStep';
import { useTheme } from 'styled-components';

interface ReportDetailsStepProps {
  payload: DataDeletionType;
  setPayload: Dispatch<SetStateAction<DataDeletionType>>;
  warningMessage: {
    title?: string;
    description: string;
  };
  reportEvents: RemovePIIReportEvents[];
  postSubmissionFiles?: FileEvidenceForDataDeletionType[];
  onTextRangeDeleteRequested: (
    range: TextRange,
    field: FieldWithTextRange,
    eventId?: string,
  ) => void;
  handleTextFieldClearAll: (
    field: FieldWithTextRange,
    eventId?: string,
  ) => void;
}

function isValidKey(key: string): key is keyof EventDeletionType {
  const validKeys: (keyof EventDeletionType)[] = [
    'eventId',
    'description',
    'emotion',
    'witnesses',
    'location',
    'externalNamedOffenders',
    'preSubmissionS3Paths',
  ];
  return validKeys.includes(key as keyof EventDeletionType);
}

export const ReportDetailsStep = ({
  payload,
  setPayload,
  warningMessage,
  reportEvents,
  postSubmissionFiles,
  onTextRangeDeleteRequested,
  handleTextFieldClearAll,
}: ReportDetailsStepProps) => {
  const theme = useTheme();
  const offenderTableHeaders = [
    'NAME',
    'DEPARTMENT',
    'SITE',
    'COUNTRY',
    'REPORT ID',
    'ADDED',
    'LINKED',
  ];

  const reportId = reportEvents[0]?.reportId || '';

  const [selectedEventId, setSelectedEventId] = useState<string | null>(
    reportEvents.length === 1 ? reportEvents[0]?.eventId || null : null,
  );

  const [selectedOffendersIds, setSelectedOffendersIds] = useState([
    ...payload.preExternalNamedOffendersIds,
    ...payload.preInternalNamedOffendersIds,
    ...payload.postExternalNamedOffendersIds,
    ...payload.postInternalNamedOffendersIds,
  ]);

  const eventOptions: DefaultOption[] = reportEvents.map((event) => ({
    label: `EVENT-ID: ${event.eventId}`,
    value: event.eventId,
  }));

  const selectedEvent = reportEvents.find(
    (event) => event.eventId === selectedEventId,
  );

  const handleSelectAll = (namedOffenders: FormattedNamedOffender[]) => {
    const eventIds = namedOffenders.map((o) => o.id.toString());
    const allSelected = eventIds.every((id) =>
      selectedOffendersIds.includes(id),
    );

    const newSelections = allSelected
      ? selectedOffendersIds.filter((id) => !eventIds.includes(id))
      : [
          ...selectedOffendersIds,
          ...eventIds.filter((id) => !selectedOffendersIds.includes(id)),
        ];
    setSelectedOffendersIds(newSelections);

    const getAllSelectedOfType = (
      type: 'Presubmission' | 'Postsubmission',
      isExternal: boolean,
    ) => {
      const isOffenderOfType = (offender: FormattedNamedOffender) =>
        offender.external === isExternal &&
        offender.events.some((ev) => ev.type === type) &&
        newSelections.includes(offender.id.toString());

      const allRelevantOffenders = reportEvents.flatMap(
        (event) => event.namedOffenders?.filter(isOffenderOfType) || [],
      );

      return [
        ...new Set(
          allRelevantOffenders.map((offender) => offender.id.toString()),
        ),
      ];
    };

    setPayload((prevPayload) => ({
      ...prevPayload,
      preInternalNamedOffendersIds: getAllSelectedOfType(
        'Presubmission',
        false,
      ),
      preExternalNamedOffendersIds: getAllSelectedOfType('Presubmission', true),
      postInternalNamedOffendersIds: getAllSelectedOfType(
        'Postsubmission',
        false,
      ),
      postExternalNamedOffendersIds: getAllSelectedOfType(
        'Postsubmission',
        true,
      ),
    }));
  };

  const isAllSelected = (namedOffenders: FormattedNamedOffender[]) => {
    return (
      namedOffenders?.length > 0 &&
      namedOffenders.every((offender) =>
        selectedOffendersIds.includes(offender.id.toString()),
      )
    );
  };

  const createOffendersTableData = (
    namedOffenders: FormattedNamedOffender[],
  ) => {
    return namedOffenders.map((offender) => {
      const isPreSubmissionOffender = offender.events
        .map((event) => event.type)
        .includes('Presubmission');

      const nameCell = (
        <div data-testid={`remove-pii-named-offenders-${offender.name}`}>
          {offender.name}
        </div>
      );

      const departmentCell = (
        <div
          data-testid={`remove-pii-named-offenders-${offender.name}-department`}
        >
          {offender.department}
        </div>
      );

      const siteCell = (
        <div data-testid={`remove-pii-named-offenders-${offender.name}-site`}>
          {offender.site}
        </div>
      );

      const countryCell = (
        <div
          data-testid={`remove-pii-named-offenders-${offender.name}-country`}
        >
          {offender.country}
        </div>
      );

      const reportIdCell = (
        <div
          data-testid={`remove-pii-named-offenders-${offender.name}-report-id`}
        >
          {offender.reports[0].id}
        </div>
      );

      const addedCell = (
        <div data-testid={`remove-pii-named-offenders-${offender.name}-added`}>
          {isPreSubmissionOffender ? 'Time of submission' : 'Post submission'}
        </div>
      );

      const linkedCell = (
        <div data-testid={`remove-pii-named-offenders-${offender.name}-linked`}>
          {!offender.external ? 'Employee linked' : 'External'}
        </div>
      );

      return {
        cellData: [
          nameCell,
          departmentCell,
          siteCell,
          countryCell,
          reportIdCell,
          addedCell,
          linkedCell,
        ],
        row: offender.id.toString(),
        data: offender,
      };
    });
  };

  const handleOffenderCheckboxSelect = (
    namedOffenders: FormattedNamedOffender[],
    id: string,
  ) => {
    const isRemovingOffender = selectedOffendersIds.includes(id);
    const newSelections = isRemovingOffender
      ? selectedOffendersIds.filter(
          (alreadySelectedId) => alreadySelectedId !== id,
        )
      : [...selectedOffendersIds, id];

    setSelectedOffendersIds(newSelections);

    const getOffendersIds = (
      type: 'Presubmission' | 'Postsubmission',
      isExternal: boolean,
    ) => {
      const eventSelectedOffenders = namedOffenders
        .filter(
          (offender) =>
            offender.external === isExternal &&
            offender.events.some((ev) => ev.type === type) &&
            newSelections.includes(offender.id.toString()),
        )
        .flatMap((offender) => offender.id.toString());

      let payloadSelectedOffenders = [];
      if (type === 'Presubmission') {
        payloadSelectedOffenders = isExternal
          ? payload.preExternalNamedOffendersIds
          : payload.preInternalNamedOffendersIds;
      } else {
        payloadSelectedOffenders = isExternal
          ? payload.postExternalNamedOffendersIds
          : payload.postInternalNamedOffendersIds;
      }

      if (isRemovingOffender) {
        payloadSelectedOffenders = payloadSelectedOffenders.filter(
          (existingId) => existingId !== id.toString(),
        );
      }

      return [
        ...new Set([...payloadSelectedOffenders, ...eventSelectedOffenders]),
      ];
    };

    setPayload((prevPayload) => ({
      ...prevPayload,
      preInternalNamedOffendersIds: getOffendersIds('Presubmission', false),
      preExternalNamedOffendersIds: getOffendersIds('Presubmission', true),
      postInternalNamedOffendersIds: getOffendersIds('Postsubmission', false),
      postExternalNamedOffendersIds: getOffendersIds('Postsubmission', true),
    }));
  };

  const renderEventDetailsItem = (
    eventItem: FieldWithTextRange,
    eventId: string,
    evidenceType?: Maybe<ParsedEvidenceDetailsType>,
  ) => {
    const value =
      !evidenceType || evidenceType === null ? '' : evidenceType.value;

    const eventDetailLabels: Record<string, string> = {
      description: 'What happened',
      emotion: 'Emotional Impact',
      witnesses: 'Witnesses',
      location: 'Event Location',
      externalNamedOffenders: 'External Named Offender',
    };
    const eventDeletionExists = payload['eventDeletions']?.find(
      (eventDeletion) => eventDeletion.eventId === eventId,
    );

    let appliedRanges: TextRange[] = [];

    if (
      eventDeletionExists &&
      isValidKey(eventItem) &&
      eventDeletionExists[eventItem]?.length
    ) {
      // Use event-specific ranges when they exist
      appliedRanges = eventDeletionExists[eventItem] as TextRange[];
    } else if (reportEvents.length === 1) {
      // Only use global payload ranges for single events as fallback
      appliedRanges = payload[eventItem] || [];
    }

    return (
      <ReportDetailsSection data-testid={`text-deletion-${eventItem}`}>
        <SectionDescription>{eventDetailLabels[eventItem]}</SectionDescription>
        <TextDeletionInput
          key={`${eventItem}-${eventId}`}
          field={eventItem}
          value={value}
          onTextRangeDeleteRequested={(range, field) =>
            onTextRangeDeleteRequested(range, field, eventId)
          }
          handleTextFieldClearAll={(eventItem) =>
            handleTextFieldClearAll(eventItem, eventId)
          }
          uniqueKey={`${eventItem}-${eventId}`}
          appliedRanges={appliedRanges}
          eventId={eventId}
        />
      </ReportDetailsSection>
    );
  };

  const renderPreSubmissionFiles = (
    eventFiles: FileEvidenceForDataDeletionType[] | undefined,
    eventId?: string,
  ) => {
    // Filter presubmission files that haven't been deleted
    const preSubmissionFiles =
      eventFiles?.filter(
        (file) => file.isPreSubmission && file.dataDeletedAt === null,
      ) ?? [];

    // Get selected presubmission files from payload
    let preSelectedFiles: string[];
    if (eventId && payload.eventDeletions) {
      const eventDeletion = payload.eventDeletions.find(
        (event) => event.eventId === eventId,
      );
      preSelectedFiles = eventDeletion
        ? [...eventDeletion.preSubmissionS3Paths]
        : [];
    } else {
      preSelectedFiles = [...(payload['preSubmissionS3Paths'] ?? [])];
    }

    return (
      <UploadedFiles
        data-testid={`presubmission-files-${eventId ?? 'no-event'}`}
        files={preSubmissionFiles}
        reportId={reportId}
        selectedFiles={preSelectedFiles}
        setSelectedFiles={(selectedFiles: string[]) => {
          const preSubmissionS3Paths = preSubmissionFiles
            .map((file) => file.fileS3Path)
            .filter((path) => selectedFiles.includes(path));

          if (eventId) {
            setPayload((prevPayload) => {
              const existingEventDeletions = prevPayload.eventDeletions || [];
              const eventIndex = existingEventDeletions.findIndex(
                (eventDeletion) => eventDeletion.eventId === eventId,
              );

              if (eventIndex >= 0) {
                const updatedEventDeletions = [...existingEventDeletions];
                updatedEventDeletions[eventIndex] = {
                  ...updatedEventDeletions[eventIndex],
                  preSubmissionS3Paths: preSubmissionS3Paths,
                };
                return {
                  ...prevPayload,
                  eventDeletions: updatedEventDeletions,
                };
              } else {
                const newEventDeletion = {
                  eventId,
                  description: [],
                  emotion: [],
                  witnesses: [],
                  location: [],
                  preSubmissionS3Paths: preSubmissionS3Paths,
                };
                return {
                  ...prevPayload,
                  eventDeletions: [...existingEventDeletions, newEventDeletion],
                };
              }
            });
          } else {
            // Handle general file selections
            setPayload((prevPayload) => ({
              ...prevPayload,
              preSubmissionS3Paths: preSubmissionS3Paths,
            }));
          }
        }}
      />
    );
  };

  const renderPostSubmissionFilesAccordion = (
    availablePostSubmissionFiles: FileEvidenceForDataDeletionType[],
  ) => {
    if (availablePostSubmissionFiles.length === 0) {
      return null;
    }

    const postSelectedFiles = payload['postSubmissionS3Paths'] ?? [];

    return (
      <Accordion
        title="Post-submission Evidence"
        subtitle={`${availablePostSubmissionFiles.length} file${
          availablePostSubmissionFiles.length !== 1 ? 's' : ''
        } available`}
        data-testid="postsubmission-files-accordion"
        borderColor={theme.atlas.semantic.color.surface.variant}
      >
        <UploadedFiles
          data-testid="postsubmission-files"
          files={availablePostSubmissionFiles}
          reportId={reportId}
          selectedFiles={postSelectedFiles}
          setSelectedFiles={(selectedFiles: string[]) => {
            const postSubmissionS3Paths = availablePostSubmissionFiles
              .map((file) => file.fileS3Path)
              .filter((path) => selectedFiles.includes(path));

            setPayload((prevPayload) => ({
              ...prevPayload,
              postSubmissionS3Paths: postSubmissionS3Paths,
            }));
          }}
        />
      </Accordion>
    );
  };

  const postSubmissionFilesToRender = (postSubmissionFiles || []).filter(
    (file) => file.dataDeletedAt === null,
  );

  return (
    <>
      <ReportDetailsHeaderContainer>
        <ReportDetailsHeaderTop>
          <StepperModalHeader data-testid="remove-pii-data-modal-header">
            Select report information to permanently remove
          </StepperModalHeader>
          {reportEvents.length > 1 && (
            <EventDropdownContainer>
              <DropdownSelect
                options={eventOptions}
                value={null}
                onChange={(option) => {
                  const selectedOption = option as DefaultOption | null;
                  setSelectedEventId(selectedOption?.value as string | null);
                }}
                buttonText={
                  selectedEventId
                    ? `EVENT-ID: ${selectedEventId}`
                    : 'SELECT EVENT'
                }
                buttonVariant="primary"
                buttonIcon="no-icon"
                data-testid="event-select-dropdown"
              />
            </EventDropdownContainer>
          )}
        </ReportDetailsHeaderTop>

        <InformationBanner
          state={FormState.WARNING}
          title={warningMessage.title ?? ''}
          description={warningMessage.description ?? ''}
          $centerIconVertically={!warningMessage.title}
        />
      </ReportDetailsHeaderContainer>

      <ReportDetailsSection>
        {reportEvents.length > 1 && !selectedEventId ? (
          <EmptyStateContainer>
            <EmptyState
              title=""
              description=""
              data-testid="no-event-selected-empty-state"
              size="small"
              notificationEmptyState={true}
            />
          </EmptyStateContainer>
        ) : selectedEvent ? (
          <>
            <EventEvidenceSection>
              <InformationBanner
                state={FormState.DEFAULT}
                description={InfoBannerText}
                $fullWidth
                $centerIconVertically
              />
              {renderEventDetailsItem(
                'location',
                selectedEvent.event,
                selectedEvent.evidence?.location,
              )}

              {renderEventDetailsItem(
                'description',
                selectedEvent.event,
                selectedEvent.evidence?.description,
              )}
              {renderEventDetailsItem(
                'emotion',
                selectedEvent.event,
                selectedEvent.evidence?.emotion,
              )}
              {renderEventDetailsItem(
                'witnesses',
                selectedEvent.event,
                selectedEvent.evidence?.witness,
              )}
            </EventEvidenceSection>

            <InformationBannerContainer>
              <InformationBanner
                state={FormState.WARNING}
                description="To delete named offender/s and supporting evidence, mark the corresponding checkbox."
                $fullWidth
                $centerIconVertically
              />
            </InformationBannerContainer>
            <EventEvidenceSection>
              <SectionTitle>Delete Named Offender/s</SectionTitle>
              {reportEvents.length > 1 && (
                <InformationBannerContainer>
                  <InformationBanner
                    state={FormState.DEFAULT}
                    description="Deleting a named offender from one event, will delete them from all events in this report."
                    $fullWidth
                    $centerIconVertically
                  />
                </InformationBannerContainer>
              )}
              <Table
                columns={offenderTableHeaders.map((header) => ({
                  label: header,
                }))}
                tableData={
                  !selectedEvent.namedOffenders ||
                  selectedEvent.namedOffenders.length === 0
                    ? []
                    : createOffendersTableData(selectedEvent.namedOffenders)
                }
                emptyText="No named offenders found"
                data-testid="offenders-table"
                allowRowSelect={true}
                selectedRows={selectedOffendersIds}
                onRowClick={(row) => {
                  handleOffenderCheckboxSelect(
                    selectedEvent.namedOffenders,
                    row,
                  );
                }}
                isFullWidth
                small
                hasStickyHeader={false}
                selectAll={() => handleSelectAll(selectedEvent.namedOffenders)}
                allSelected={isAllSelected(selectedEvent.namedOffenders)}
              />
              {selectedEvent.nonSeparatedExternalNamedOffenders.length > 0 &&
                renderEventDetailsItem(
                  'externalNamedOffenders',
                  selectedEvent.event,
                  {
                    value:
                      selectedEvent.nonSeparatedExternalNamedOffenders[0].name,
                  } as ParsedEvidenceDetailsType,
                )}
            </EventEvidenceSection>
            <EventEvidenceSection>
              <SectionTitle>Delete Evidence</SectionTitle>
              {renderPreSubmissionFiles(
                selectedEvent.files,
                selectedEvent.event,
              )}
            </EventEvidenceSection>
            {renderPostSubmissionFilesAccordion(postSubmissionFilesToRender)}
          </>
        ) : null}
      </ReportDetailsSection>
    </>
  );
};
